import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import {
  Search,
  Plus,
  Target,
  CheckCircle,
  XCircle,
  DollarSign,
  ChevronRight,
  Loader2,
  AlertCircle,
  RefreshCw,
} from 'lucide-react';
import { cn, formatDate, formatCurrency } from '@/lib/utils';
import {
  GET_OPPORTUNITIES,
  MARK_OPPORTUNITY_WON,
  MARK_OPPORTUNITY_LOST,
  type OpportunitiesQueryResult,
  type OpportunityStage,
} from '@/graphql/opportunities';

const STAGE_BADGES: Record<OpportunityStage, { label: string; className: string }> = {
  DISCOVERY: { label: 'Discovery', className: 'badge-primary' },
  PROPOSAL: { label: 'Proposal', className: 'badge-warning' },
  NEGOTIATION: { label: 'Negotiation', className: 'bg-violet/10 text-violet dark:bg-violet-light/20 dark:text-violet-light' },
  WON: { label: 'Won', className: 'badge-secondary' },
  LOST: { label: 'Lost', className: 'badge-neutral' },
};

export function OpportunityList() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [stageFilter, setStageFilter] = useState<string>('active');

  // GraphQL query
  const { data, loading, error, refetch } = useQuery<OpportunitiesQueryResult>(
    GET_OPPORTUNITIES,
    {
      variables: {
        stage: stageFilter,
        search: searchQuery || undefined,
        limit: 50,
      },
      fetchPolicy: 'cache-and-network',
    }
  );

  // GraphQL mutations
  const [markWon, { loading: markingWon }] = useMutation(MARK_OPPORTUNITY_WON, {
    onCompleted: () => refetch(),
  });

  const [markLost, { loading: markingLost }] = useMutation(MARK_OPPORTUNITY_LOST, {
    onCompleted: () => refetch(),
  });

  const opportunities = data?.opportunities?.items || [];
  const pageInfo = data?.opportunities?.pageInfo;

  // Calculate pipeline value (weighted by probability)
  const pipelineValue = opportunities
    .filter((o) => !['WON', 'LOST'].includes(o.stage))
    .reduce((sum, o) => sum + o.value * (o.probability / 100), 0);

  const handleMarkWon = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (confirm('Mark this opportunity as won?')) {
      await markWon({ variables: { id } });
    }
  };

  const handleMarkLost = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const reason = prompt('Lost reason (optional):');
    const competitor = prompt('Lost to competitor (optional):');
    await markLost({
      variables: {
        id,
        input: {
          lostReason: reason || undefined,
          lostToCompetitorName: competitor || undefined,
        },
      },
    });
  };

  // Loading state
  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-pond" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="card p-8 text-center">
        <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-4" />
        <p className="text-red-600 dark:text-red-400 mb-4">
          Error loading opportunities: {error.message}
        </p>
        <button onClick={() => refetch()} className="btn-primary">
          <RefreshCw className="w-4 h-4" />
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate dark:text-mist">
            Opportunities
          </h1>
          <p className="text-chalkboard dark:text-rain-cloud mt-1">
            {pageInfo?.totalCount || 0} opportunit{pageInfo?.totalCount !== 1 ? 'ies' : 'y'} • 
            Pipeline: {formatCurrency(pipelineValue)}
          </p>
        </div>
        <button 
          onClick={() => navigate('/sales/prospects')}
          className="btn-primary"
        >
          <Plus className="w-4 h-4" />
          Add Opportunity
        </button>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-body">
          <div style={{ display: 'flex', flexDirection: 'row', gap: '1rem', alignItems: 'center' }}>
            <div className="relative" style={{ flex: '1 1 auto', minWidth: 0 }}>
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-french-gray pointer-events-none" />
              <input
                type="text"
                placeholder="Search opportunities..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="input"
                style={{ width: '100%', paddingLeft: '2.5rem' }}
              />
            </div>
            <select
              value={stageFilter}
              onChange={(e) => setStageFilter(e.target.value)}
              className="select"
              style={{ flex: '0 0 auto', width: '12rem' }}
            >
              <option value="active">Active Pipeline</option>
              <option value="">All Stages</option>
              <option value="discovery">Discovery</option>
              <option value="proposal">Proposal</option>
              <option value="negotiation">Negotiation</option>
              <option value="won">Won</option>
              <option value="lost">Lost</option>
            </select>
            {loading && (
              <Loader2 className="w-5 h-5 animate-spin text-pond flex-shrink-0" />
            )}
          </div>
        </div>
      </div>

      {/* Opportunities Table */}
      <div className="card overflow-hidden">
        <table className="table">
          <thead>
            <tr>
              <th>Opportunity</th>
              <th>Stage</th>
              <th>Value</th>
              <th>Probability</th>
              <th>Expected Close</th>
              <th className="w-24">Actions</th>
            </tr>
          </thead>
          <tbody>
            {opportunities.map((opp) => {
              const stageBadge = STAGE_BADGES[opp.stage];
              const contactName = opp.primaryContact
                ? `${opp.primaryContact.firstName} ${opp.primaryContact.lastName}`
                : 'No contact';

              return (
                <tr
                  key={opp.id}
                  onClick={() => navigate(`/sales/opportunities/${opp.id}`)}
                  className="cursor-pointer hover:bg-pale-gray-1 dark:hover:bg-navy/50"
                >
                  <td>
                    <div className="flex items-center gap-2">
                      <Target className="w-4 h-4 text-chalkboard dark:text-rain-cloud" />
                      <div>
                        <span className="font-medium text-slate dark:text-mist block">
                          {opp.organization.name}
                        </span>
                        <span className="text-xs text-chalkboard dark:text-rain-cloud">
                          {opp.name} • {contactName}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className={cn('badge', stageBadge.className)}>
                      {stageBadge.label}
                    </span>
                    {opp.lostToCompetitorName && (
                      <p className="text-xs text-rust dark:text-salmon mt-1">
                        Lost to: {opp.lostToCompetitorName}
                      </p>
                    )}
                    {opp.isRecurring && (
                      <p className="text-xs text-turquoise dark:text-aqua mt-1">
                        Annual Recurring
                      </p>
                    )}
                  </td>
                  <td>
                    <div className="flex items-center gap-1">
                      <DollarSign className="w-4 h-4 text-turquoise dark:text-aqua" />
                      <span className="font-medium text-slate dark:text-mist">
                        {formatCurrency(opp.value)}
                      </span>
                    </div>
                  </td>
                  <td>
                    <div className="flex items-center gap-2">
                      <div className="w-16 h-2 bg-gray-light dark:bg-night-pond rounded-full overflow-hidden">
                        <div
                          className={cn(
                            'h-full rounded-full',
                            opp.probability >= 75 ? 'bg-turquoise dark:bg-aqua' :
                            opp.probability >= 50 ? 'bg-yellow' :
                            opp.probability >= 25 ? 'bg-orange' : 'bg-rust dark:bg-salmon'
                          )}
                          style={{ width: `${opp.probability}%` }}
                        />
                      </div>
                      <span className="text-sm text-chalkboard dark:text-rain-cloud">
                        {opp.probability}%
                      </span>
                    </div>
                  </td>
                  <td className="text-sm text-chalkboard dark:text-rain-cloud">
                    {formatDate(new Date(opp.expectedCloseDate))}
                  </td>
                  <td>
                    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                      {!['WON', 'LOST'].includes(opp.stage) && (
                        <>
                          <button 
                            className="btn-ghost btn-icon btn-sm text-turquoise dark:text-aqua" 
                            title="Mark as Won"
                            onClick={(e) => handleMarkWon(opp.id, e)}
                            disabled={markingWon}
                          >
                            <CheckCircle className="w-4 h-4" />
                          </button>
                          <button 
                            className="btn-ghost btn-icon btn-sm text-rust dark:text-salmon" 
                            title="Mark as Lost"
                            onClick={(e) => handleMarkLost(opp.id, e)}
                            disabled={markingLost}
                          >
                            <XCircle className="w-4 h-4" />
                          </button>
                        </>
                      )}
                      <ChevronRight className="w-4 h-4 text-chalkboard dark:text-rain-cloud" />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {opportunities.length === 0 && !loading && (
          <div className="p-8 text-center">
            <Target className="w-12 h-12 text-french-gray mx-auto mb-4" />
            <p className="text-chalkboard dark:text-rain-cloud mb-2">No opportunities found.</p>
            <p className="text-sm text-french-gray">
              Create opportunities from the Prospects page.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
